import * as React from 'react';
import { action } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as classNames from 'classnames';
import { CSSTransitionGroup } from 'react-transition-group';
import * as dateParse from 'date-fns/parse';
import * as dateFormat from 'date-fns/format';
import { Modal } from '../../popup';
import { LoadingSpinner, SavingIndicator, SavingIndicatorStatus } from '../../indicator';
import { EOrderStatusChangeStatus } from '../../../../../common/interfaces/order';
import { Button, EButtonType } from '../../action';
import { OrderItemsGroups } from '../../item';
import { Pagination } from '../../pagination';
import { IAppOnlyStore } from '../../../stores/AllStores';
import { Picker } from '../../forms';

// Styles
const s = require('./OrdersHistory.css');
const expandArrows = require('./../../../assets/expand-arrows.svg');
const collapseArrows = require('./../../../assets/collapse-arrows.svg');

// Component
@inject('store')
@observer
export class OrdersHistory extends React.Component<IAppOnlyStore, {}> {
    private modal: Modal = null;
    private referenceModal = (ref: Modal) => (this.modal = ref);

    private orderDissmisActionsAction = {
        key: 'dismiss',
        value: null,
        title: 'Dismiss',
        subline: 'Closes this menu and does nothing'
    };

    render() {
        const { orders } = this.props.store;

        return (
            <Modal
                ref={this.referenceModal}
                title="Orders history"
                show={orders.showOrdersHistoryModal}
                onClose={this.handleOrdersHistoryModalClose}
                onModalContainerClick={this.handleContainerClick}
                closeLabel="Close"
            >
                <CSSTransitionGroup
                    component="div"
                    className={s.container}
                    transitionName="fade"
                    transitionAppear={true}
                    transitionEnter={true}
                    transitionLeave={true}
                    transitionAppearTimeout={400}
                    transitionEnterTimeout={400}
                    transitionLeaveTimeout={300}
                >
                    <div key="orders-loading" className={classNames(s.loading, { [s.active]: orders.loading })}>
                        <LoadingSpinner active={orders.loading} size={64} />
                        <br />
                        <p>Loading orders...</p>
                    </div>

                    {orders.ordersHistory &&
                        orders.ordersHistory.length > 0 && (
                            <div key="orders-list" className={s.orders}>
                                {orders.ordersHistory.map(order => {
                                    // Parse created at date
                                    const createdAtDate = dateParse(order.createdAt);

                                    // Prepare order actions for orders that are still being processed
                                    const orderActions =
                                        order.status > 0 && order.status < 3
                                            ? [
                                                  ...[this.orderDissmisActionsAction],
                                                  ...[
                                                      {
                                                          key: 'cancel',
                                                          value: 0,
                                                          title: order.status > 1 ? 'Request cancellation' : 'Cancel order',
                                                          subline:
                                                              order.status > 1
                                                                  ? 'Informs us about request to cancel the order'
                                                                  : 'Removes order from processing'
                                                      }
                                                  ]
                                              ]
                                            : [];

                                    // Check if order status is being changed
                                    const orderStatusIsBeingChanged = orders.ordersHavingStatusChangedIds.indexOf(order.id);
                                    const orderStatusIsBeingChangedStatus: EOrderStatusChangeStatus =
                                        orderStatusIsBeingChanged !== -1
                                            ? orders.ordersStatusChangeStatuses[orderStatusIsBeingChanged].status
                                            : EOrderStatusChangeStatus.None;

                                    return (
                                        <div key={order.id} className={s.order}>
                                            <div className={s.header}>
                                                <div className={s.left}>
                                                    <h3>
                                                        {`Order #${order.id} - `}
                                                        <strong
                                                            className={classNames({
                                                                [s.cancelled]: order.status === 0,
                                                                [s.processing]: order.status > 1 && order.status < 4,
                                                                [s.shipped]: order.status === 4
                                                            })}
                                                        >
                                                            {order.statusName}
                                                        </strong>
                                                    </h3>
                                                    {order.poNumber && <h4>{`PO# ${order.poNumber}`}</h4>}
                                                    <div>
                                                        <h5>{`Placed at ${dateFormat(createdAtDate, 'MM/DD/YYYY h:mm a')}`}</h5>
                                                        <SavingIndicator
                                                            labels={{
                                                                success: order.status === 0 ? 'Cancelled' : 'Reopened',
                                                                saving: order.status === 0 ? 'Reopening' : 'Cancelling',
                                                                error:
                                                                    order.status === 0
                                                                        ? 'Could not reopen, try again'
                                                                        : 'Could not cancel, try again'
                                                            }}
                                                            status={
                                                                orderStatusIsBeingChangedStatus ===
                                                                EOrderStatusChangeStatus.Saving
                                                                    ? SavingIndicatorStatus.Saving
                                                                    : orderStatusIsBeingChangedStatus ===
                                                                      EOrderStatusChangeStatus.Error
                                                                        ? SavingIndicatorStatus.Error
                                                                        : orderStatusIsBeingChangedStatus ===
                                                                          EOrderStatusChangeStatus.Success
                                                                            ? SavingIndicatorStatus.Success
                                                                            : SavingIndicatorStatus.None
                                                            }
                                                        />
                                                    </div>
                                                    <br />
                                                    <Button
                                                        onClick={this.handleOrdersHistoryItemsToggle(order.id)}
                                                        type={EButtonType.Action}
                                                        icon={{
                                                            element: (
                                                                <img
                                                                    src={order.itemsExpanded ? collapseArrows : expandArrows}
                                                                    width={11}
                                                                    height={11}
                                                                    alt=""
                                                                />
                                                            )
                                                        }}
                                                    >
                                                        {order.itemsExpanded ? 'Hide items' : 'Show items'}
                                                    </Button>
                                                </div>

                                                <div className={s.right}>
                                                    {orderActions.length > 0 && (
                                                        <Picker
                                                            onContainerClick={this.handleStatusPickerClick}
                                                            show={
                                                                orders.showOpenStatusPickerForOrderId.indexOf(order.id) !== -1
                                                            }
                                                            valueSelected="none"
                                                            onValueSelectedChange={this.handleStatusChange(order.id)}
                                                            field={{
                                                                label: 'Order actions',
                                                                value:
                                                                    order.status > 1 ? 'Request cancellation' : 'Cancel order',
                                                                onFocus: this.handleStatusFieldFocus(order.id)
                                                            }}
                                                            options={[
                                                                {
                                                                    name: 'Actions',
                                                                    options: orderActions
                                                                }
                                                            ]}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div className={s.items}>
                                                {order.itemsExpanded && (
                                                    <SavingIndicator
                                                        className={s.itemsLoading}
                                                        status={
                                                            order.itemsLoading
                                                                ? SavingIndicatorStatus.Saving
                                                                : SavingIndicatorStatus.None
                                                        }
                                                        labels={{
                                                            saving:
                                                                order.itemsEntries.length > 0
                                                                    ? 'Refreshing items...'
                                                                    : 'Loading items...'
                                                        }}
                                                    />
                                                )}

                                                {order.itemsExpanded && (
                                                    <OrderItemsGroups
                                                        className={s.itemsGroups}
                                                        items={order.itemsEntries.map(item => ({
                                                            id: item.id,
                                                            itemCode: item.itemcode,
                                                            itemName: item.itemname,
                                                            itemClass: null,
                                                            image: item.image,
                                                            imageThumb: item.image,
                                                            Partslink: item.partsLink,
                                                            certCategory: item.certification,
                                                            certification: item.certification,
                                                            custItemCode: item.customerItemcode,
                                                            dpi: item.dpi,
                                                            oem: item.oem,
                                                            fromYear: item.fromYear,
                                                            toYear: item.toYear,
                                                            listPrice: item.price.toString(),
                                                            price: item.price.toString(),
                                                            make: item.make,
                                                            model: item.model,
                                                            docduedate: null,
                                                            quantity: item.quantity,
                                                            hidden: false,
                                                            stock: {},
                                                            fitments: []
                                                        }))}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {orders.ordersHistoryTotal > orders.ordersPerPageCount && (
                                    <Pagination
                                        className={s.pagination}
                                        entriesLoading={orders.loading}
                                        currentPage={orders.ordersHistoryPage}
                                        countPerPage={orders.ordersPerPageCount}
                                        countTotal={orders.ordersHistoryTotal}
                                        onPageChange={this.handleOrdersHistoryPageChange}
                                    />
                                )}
                            </div>
                        )}
                </CSSTransitionGroup>
            </Modal>
        );
    }

    private handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const { orders } = this.props.store;
        orders.showOpenStatusPickerForOrderId = [];
    };

    private handleStatusPickerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    private handleStatusFieldFocus = (orderId: number) => (e: React.FocusEvent<HTMLInputElement>) => {
        const { orders } = this.props.store;
        orders.showOpenStatusPickerForOrderId = [...orders.showOpenStatusPickerForOrderId, orderId];
    };

    @action
    private handleStatusChange = (orderId: number) => (newValue: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
        const { orders } = this.props.store;

        // Close menu
        const orderFound = orders.showOpenStatusPickerForOrderId.indexOf(orderId);
        if (orderFound !== -1) {
            orders.showOpenStatusPickerForOrderId = [
                ...orders.showOpenStatusPickerForOrderId.slice(0, orderFound),
                ...orders.showOpenStatusPickerForOrderId.slice(orderFound + 1)
            ];
        }

        // Cancel or reopen depending on newValue
        if (newValue !== null) {
            orders.cancelOrReopenOrder(orderId, newValue === 0);
        }
    };

    @action
    private handleOrdersHistoryModalClose = () => {
        this.props.store.orders.hideOrdersHistoryModal();
    };

    @action
    private handleOrdersHistoryItemsToggle = (orderId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.orders.fetchOrderItems(orderId);
    };

    @action
    private handleOrdersHistoryPageChange = (newPage: number) => {
        this.props.store.orders.changeOrdersHistoryPage(newPage);

        if (this.modal && typeof this.modal.scrollToTop === 'function') {
            this.modal.scrollToTop();
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/layout/OrdersHistory/OrdersHistory.tsx